/**
 * PWA 注册（手写，零依赖）
 *
 * 三条硬约束：
 *  1. **桌面端绝不注册**：桌面端与网页端共用同一份 dist，但桌面端由本机后端
 *     以 http://127.0.0.1:<port> 提供。在那里装 SW 会把旧版资产钉死在 Cache
 *     Storage 里，升级后出现「壳是新的、资源是旧的」——正是本项目缓存类血泪
 *     （#49/#67）的形态。故用 window.psbDesktop.isDesktop 闸掉。
 *  2. **只在安全上下文注册**：https 或 localhost/127.0.0.1，其余直接跳过。
 *  3. **不自动 skipWaiting**：新版本就绪时弹提示，由用户点「刷新」再接管。
 */

type PsbDesktop = { isDesktop?: boolean };

const DESKTOP_KEY = 'molore:pwa:a2hs-dismissed';

function isDesktopApp(): boolean {
  return !!(window as unknown as { psbDesktop?: PsbDesktop }).psbDesktop?.isDesktop;
}

function isSecureContextOk(): boolean {
  const h = location.hostname;
  return (
    location.protocol === 'https:' ||
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '[::1]'
  );
}

/** 极简 toast：不依赖 React，可在首屏渲染前调用 */
function showUpdateBar(onRefresh: () => void): void {
  if (document.getElementById('pwa-update-bar')) return;

  const bar = document.createElement('div');
  bar.id = 'pwa-update-bar';
  bar.setAttribute('role', 'status');
  bar.style.cssText = [
    'position:fixed',
    'left:50%',
    'bottom:calc(16px + env(safe-area-inset-bottom, 0px))',
    'transform:translateX(-50%)',
    'z-index:2147483000',
    'display:flex',
    'align-items:center',
    'gap:12px',
    'max-width:calc(100vw - 32px)',
    'padding:10px 12px 10px 16px',
    'border-radius:999px',
    'border:1px solid #4a423a',
    'background:#1d1a16',
    'color:#ece7df',
    'font-size:13px',
    'line-height:1.4',
    'box-shadow:0 8px 28px rgba(0,0,0,.45)',
    'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI","PingFang SC","Microsoft YaHei",sans-serif',
  ].join(';');

  const text = document.createElement('span');
  text.textContent = '有新版本可用';

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = '刷新';
  btn.style.cssText = [
    'appearance:none',
    'border:0',
    'cursor:pointer',
    'padding:6px 14px',
    'border-radius:999px',
    'background:#ece7df',
    'color:#12100e',
    'font:inherit',
    'font-weight:600',
  ].join(';');
  btn.addEventListener('click', onRefresh);

  const close = document.createElement('button');
  close.type = 'button';
  close.setAttribute('aria-label', '关闭');
  close.textContent = '×';
  close.style.cssText = [
    'appearance:none',
    'border:0',
    'background:transparent',
    'color:#a89f93',
    'cursor:pointer',
    'font-size:18px',
    'line-height:1',
    'padding:0 4px',
  ].join(';');
  close.addEventListener('click', () => bar.remove());

  bar.append(text, btn, close);
  document.body.appendChild(bar);
}

/** iOS Safari 没有 beforeinstallprompt，给一条「添加到主屏幕」的一次性提示 */
function maybeShowIosHint(): void {
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in document);
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  if (!isIos || standalone) return;
  try {
    if (localStorage.getItem(DESKTOP_KEY) === '1') return;
  } catch {
    return;
  }

  const bar = document.createElement('div');
  bar.style.cssText = [
    'position:fixed',
    'left:50%',
    'bottom:calc(16px + env(safe-area-inset-bottom, 0px))',
    'transform:translateX(-50%)',
    'z-index:2147483000',
    'max-width:calc(100vw - 32px)',
    'padding:10px 14px',
    'border-radius:14px',
    'border:1px solid #4a423a',
    'background:#1d1a16',
    'color:#ece7df',
    'font-size:13px',
    'line-height:1.5',
    'box-shadow:0 8px 28px rgba(0,0,0,.45)',
    'font-family:-apple-system,BlinkMacSystemFont,"PingFang SC","Microsoft YaHei",sans-serif',
  ].join(';');
  bar.textContent = '装到主屏幕：点底部「分享」→「添加到主屏幕」';
  bar.addEventListener('click', () => {
    try {
      localStorage.setItem(DESKTOP_KEY, '1');
    } catch {
      /* ignore */
    }
    bar.remove();
  });
  document.body.appendChild(bar);
}

export function registerPwa(): void {
  if (isDesktopApp()) return; // 约束 1
  if (!('serviceWorker' in navigator)) return;
  if (!isSecureContextOk()) return; // 约束 2

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'imports' })
      .then((reg) => {
        // 已有等待中的新版本（例如上次没点刷新）
        if (reg.waiting && navigator.serviceWorker.controller) {
          showUpdateBar(() => {
            reg.waiting?.postMessage('SKIP_WAITING');
          });
        }

        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // installed 且已有 controller ⇒ 这是「更新」而非首次安装（约束 3）
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              showUpdateBar(() => {
                installing.postMessage('SKIP_WAITING');
              });
            }
          });
        });

        // 新 SW 接管后重载一次，确保 HTML 与 chunk 同代
        let reloading = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (reloading) return;
          reloading = true;
          location.reload();
        });
      })
      .catch(() => {
        /* 注册失败不影响主流程 */
      });

    maybeShowIosHint();
  });
}
